import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { isDemoSite } from '../lib/isDemoSite'

export type UserRole = 'creator' | 'admin' | 'client' | 'operator' | null

interface AuthContextType {
    user: User | null
    session: Session | null
    role: UserRole
    clientId: string | null
    /** portal_operators.id when role === 'operator'. */
    operatorId: string | null
    /** Tenant ID for multi-tenant scoping. Null only during demo or before resolution. */
    companyId: string | null
    /** True when role === 'creator' (platform owner). Portal Configuration and creator-only notifications. */
    isCreator: boolean
    /** False only for creator accounts with can_write = false. True for all other roles. */
    canWrite: boolean
    isSuperManager: boolean
    allowedClientIds: string[]
    /** True when running on the demo site with no authenticated user. */
    isDemo: boolean
    loading: boolean
    /** True for the ~300ms window after signOut() is called, before the auth state resolves.
     *  Use to fade out shaders / heavy assets before unmounting. */
    isSigningOut: boolean
    signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    session: null,
    role: null,
    clientId: null,
    operatorId: null,
    companyId: null,
    isCreator: false,
    canWrite: true,
    isSuperManager: false,
    allowedClientIds: [],
    isDemo: false,
    loading: true,
    isSigningOut: false,
    signOut: async () => { },
})

export function AuthProvider({ children }: { children: ReactNode }) {
    const demo = isDemoSite()
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [role, setRole] = useState<UserRole>(demo ? 'admin' : null)
    const [clientId, setClientId] = useState<string | null>(null)
    const [operatorId, setOperatorId] = useState<string | null>(null)
    const [isCreator, setIsCreator] = useState(false)
    const [canWrite, setCanWrite] = useState(true)
    const [isSuperManager, setIsSuperManager] = useState(demo)
    const [allowedClientIds, setAllowedClientIds] = useState<string[]>([])
    const [companyId, setCompanyId] = useState<string | null>(null)
    const [isDemo, setIsDemo] = useState(demo)
    const [loading, setLoading] = useState(true)
    const [isSigningOut, setIsSigningOut] = useState(false)

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            setUser(session?.user ?? null)
            if (session?.user) {
                setIsDemo(false)
                resolveUserRole(session.user.email!)
            } else if (demo) {
                setRole('admin')
                setIsCreator(false)
                setIsSuperManager(true)
                setIsDemo(true)
                setCompanyId('00000000-0000-0000-0000-000000000001')
                setLoading(false)
            } else {
                setRole(null)
                setClientId(null)
                setIsCreator(false)
                setIsSuperManager(false)
                setAllowedClientIds([])
                setLoading(false)
            }
        })

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session)
            setUser(session?.user ?? null)
            if (session?.user) {
                setIsDemo(false)
                // TOKEN_REFRESHED and USER_UPDATED don't change the role — skip the DB round-trip
                // and the loading spinner that would unmount the current page.
                if (event !== 'TOKEN_REFRESHED' && event !== 'USER_UPDATED') {
                    setLoading(true)
                    resolveUserRole(session.user.email!)
                }
            } else if (demo) {
                setRole('admin')
                setIsCreator(false)
                setIsSuperManager(true)
                setIsDemo(true)
                setCompanyId('00000000-0000-0000-0000-000000000001')
                setLoading(false)
            } else {
                setRole(null)
                setClientId(null)
                setIsCreator(false)
                setIsSuperManager(false)
                setAllowedClientIds([])
                setLoading(false)
            }
        })

        return () => subscription.unsubscribe()
    }, [])

    async function resolveUserRole(email: string) {
        if (!email) {
            setRole(null)
            setClientId(null)
            setOperatorId(null)
            setCompanyId(null)
            setIsCreator(false)
            setIsSuperManager(false)
            setAllowedClientIds([])
            setLoading(false)
            return
        }

        try {
            setCanWrite(true) // reset; only overridden below for read-only creators
            // 1. Check portal_creators (platform owner; distinct from tenant super admins)
            const { data: creatorRow } = await supabase
                .from('portal_creators')
                .select('id, company_id, can_write')
                .eq('email', email)
                .limit(1)
                .maybeSingle()

            if (creatorRow) {
                setRole('creator')
                setClientId(null)
                setOperatorId(null)
                setCompanyId(creatorRow.company_id ?? null)
                setIsCreator(true)
                setCanWrite(creatorRow.can_write ?? true)
                setIsSuperManager(false)
                setAllowedClientIds([])
                setLoading(false)
                return
            }

            // 2. Bootstrap: creator email when portal_creators not yet populated
            const CREATOR_EMAILS = ['ceo@luster.company']
            if (CREATOR_EMAILS.includes(email)) {
                setRole('creator')
                setClientId(null)
                setOperatorId(null)
                setCompanyId('11111111-1111-1111-1111-111111111111')
                setIsCreator(true)
                setIsSuperManager(false)
                setAllowedClientIds([])
                setLoading(false)
                return
            }

            // 3. Check portal_admins table (DB-driven admin list)
            const { data: adminRow } = await supabase
                .from('portal_admins')
                .select('id, is_super_manager, company_id')
                .eq('email', email)
                .limit(1)
                .maybeSingle()

            if (adminRow) {
                setRole('admin')
                setClientId(null)
                setOperatorId(null)
                setCompanyId(adminRow.company_id ?? null)
                setIsCreator(false)
                setIsSuperManager(!!adminRow.is_super_manager)
                if (adminRow.is_super_manager) {
                    setAllowedClientIds([]) // empty = all
                } else {
                    const { data: links } = await supabase
                        .from('portal_admin_clients')
                        .select('client_id')
                        .eq('admin_id', adminRow.id)
                    const linkIds = (links || []).map((r) => r.client_id)
                    if (linkIds.length === 0) {
                        setAllowedClientIds([])
                    } else {
                        const { data: clients } = await supabase
                            .from('clients')
                            .select('id')
                            .in('id', linkIds)
                            .eq('is_demo', isDemoSite())
                        setAllowedClientIds((clients || []).map((c) => c.id))
                    }
                }
                setLoading(false)
                return
            }

            // 4. Check clients table (Client Access) — scope by demo vs portal. Must run before ADMIN_DOMAINS
            //    so that demo client emails resolve as client, not admin.
            const { data: clientRows } = await supabase
                .from('clients')
                .select('id, company_id')
                .eq('email', email)
                .eq('is_demo', isDemoSite())
                .limit(1)

            if (clientRows && clientRows.length > 0) {
                setRole('client')
                setClientId(clientRows[0].id)
                setOperatorId(null)
                setCompanyId(clientRows[0].company_id ?? null)
                setIsCreator(false)
                setIsSuperManager(false)
                setAllowedClientIds([])
                setLoading(false)
                return
            }

            // 5. Fallback: hardcoded bootstrap admins (tenant admins; treated as super manager until added to portal_admins)
            const ADMIN_EMAILS = ['art@luster.company']
            if (ADMIN_EMAILS.includes(email)) {
                setRole('admin')
                setClientId(null)
                setOperatorId(null)
                setCompanyId('00000000-0000-0000-0000-000000000001')
                setIsCreator(false)
                setIsSuperManager(true)
                setAllowedClientIds([])
                setLoading(false)
                return
            }

            // 6. Check portal_operators (Operator access — minimal report/activity portal)
            const { data: operatorRow } = await (supabase as any)
                .from('portal_operators')
                .select('id, company_id')
                .eq('email', email)
                .limit(1)
                .maybeSingle()

            if (operatorRow) {
                setRole('operator')
                setClientId(null)
                setOperatorId(operatorRow.id)
                setCompanyId(operatorRow.company_id ?? null)
                setIsCreator(false)
                setIsSuperManager(false)
                setAllowedClientIds([])
                setLoading(false)
                return
            }

            setRole(null)
            setClientId(null)
            setOperatorId(null)
            setCompanyId(null)
            setIsCreator(false)
            setIsSuperManager(false)
            setAllowedClientIds([])
        } catch (error) {
            console.error('Error resolving role:', error)
            setRole(null)
            setClientId(null)
            setOperatorId(null)
            setCompanyId(null)
            setIsCreator(false)
            setIsSuperManager(false)
            setAllowedClientIds([])
        } finally {
            setLoading(false)
        }
    }

    async function signOut() {
        setIsSigningOut(true)
        // Small delay lets heavy assets (shaders, Rive) fade/unmount gracefully
        // before the auth state flush re-renders the login page.
        await new Promise((r) => setTimeout(r, 280))
        await supabase.auth.signOut()
        setIsSigningOut(false)
        // Remaining state updates handled by onAuthStateChange
    }

    return (
        <AuthContext.Provider value={{ user, session, role, clientId, operatorId, companyId, isCreator, canWrite, isSuperManager, allowedClientIds, isDemo, loading, isSigningOut, signOut }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
